import { useState, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Tooltip,
  Typography,
  Divider,
  Chip,
  IconButton,
  Popover,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { Home, PanelLeftOpen, PanelLeftClose } from "lucide-react";
import { topLevelItems, navigationGroups } from "../../config/navigation";
import { usePermissions } from "../../contexts/permissions-context";
import logo from "../../assets/logo-nemesis.svg";
interface MuiSidebarProps {
  isPinned: boolean;
  onTogglePin: () => void;
}
export function MuiSidebar({
  isPinned,
  onTogglePin,
}: MuiSidebarProps) {
  const location = useLocation();
  const pathname = location.pathname;
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const { canAccess } = usePermissions();

  const filteredTopItems = useMemo(
    () => topLevelItems.filter((item) => canAccess(item.href)),
    [canAccess],
  );

  const filteredGroups = useMemo(
    () =>
      navigationGroups
        .map((group) => ({
          ...group,
          items: group.items.filter((item) => canAccess(item.href)),
        }))
        .filter((group) => group.items.length > 0),
    [canAccess],
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [popoverGroupTitle, setPopoverGroupTitle] = useState<string>("");
  const toggleGroup = (groupTitle: string) => {
    setOpenGroups((prev) => ({
      ...prev,
      [groupTitle]: !prev[groupTitle],
    }));
  };
  const handlePopoverToggle = (event: React.MouseEvent<HTMLElement>, groupTitle: string) => {
    if (anchorEl && popoverGroupTitle === groupTitle) {
      // Si ya está abierto, cerrarlo
      setAnchorEl(null);
      setPopoverGroupTitle("");
    } else {
      // Si está cerrado o es otro grupo, abrirlo
      setAnchorEl(event.currentTarget);
      setPopoverGroupTitle(groupTitle);
    }
  };
  const handlePopoverClose = () => {
    setAnchorEl(null);
    setPopoverGroupTitle("");
  };
  const openPopover = Boolean(anchorEl);
  // El sidebar se controla únicamente con isPinned
  const drawerWidth = isPinned ? 280 : 60;
  const showText = isPinned;
  return (
    <Box
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "& .MuiDrawer-paper": {
          width: drawerWidth,
          boxSizing: "border-box",
          transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          overflowX: "hidden",
          borderRight: "1px solid",
          borderColor: "divider",
        },
      }}
    >
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            overflowX: "hidden",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* Logo Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: showText ? "flex-start" : "center",
            px: showText ? 2 : 1,
            height: 56,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          {showText ? (
            <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
              <img
                src={logo}
                alt="Némesis Logo"
                style={{ height: 24, width: 24 }}
              />
              <Typography
                variant="h6"
                sx={{ ml: 1, fontWeight: 700, color: "text.primary" }}
              >
                Némesis
              </Typography>
            </Box>
          ) : (
            <Tooltip title="Némesis" placement="right">
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                }}
              >
                <img
                  src={logo}
                  alt="Némesis Logo"
                  style={{ height: 28, width: 28 }}
                />
              </Box>
            </Tooltip>
          )}
        </Box>
        {/* Home Link */}
        <List sx={{ pt: 2 }}>
          <ListItem disablePadding>
            <Tooltip title={!showText ? "Inicio" : ""} placement="right">
              <ListItemButton
                component={Link}
                to="/dashboard"
                selected={pathname === "/dashboard"}
                sx={{
                  minHeight: 48,
                  justifyContent: !showText ? "center" : "initial",
                  px: !showText ? 1.5 : 2,
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: !showText ? 0 : 2,
                    justifyContent: "center",
                  }}
                >
                  <Home
                    className={`h-5 w-5 ${
                      pathname === "/home" ? "text-nemesis-primary" : ""
                    }`}
                  />
                </ListItemIcon>
                {showText && <ListItemText primary="Inicio" />}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        </List>
        <Divider
          sx={{
            my: 1,
            mx: !showText ? 1 : 0,
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            opacity: 1,
            width: "auto",
            willChange: "margin, width",
          }}
        />
        {/* Top Level Navigation */}
        <List sx={{ px: 1 }}>
          {filteredTopItems
            .filter((item) => item.title && item.title.trim() !== "")
            .map((item) => {
            const isActive =
              pathname === item.href || pathname.startsWith(item.href + "/");
            const isDisabled = item.disabled || false;
            return (
              <ListItem key={item.href} disablePadding>
                <Tooltip
                  title={
                    !showText
                      ? item.comingSoon
                        ? `${item.title} (Próximamente)`
                        : item.title
                      : ""
                  }
                  placement="right"
                >
                  <ListItemButton
                    component={isDisabled ? "div" : Link}
                    {...(!isDisabled ? { to: item.href } : {})}
                    selected={isActive && !isDisabled}
                    disabled={isDisabled}
                    sx={{
                      minHeight: 44,
                      justifyContent: !showText ? "center" : "initial",
                      px: !showText ? 1.5 : 2,
                      borderRadius: 1,
                      mb: 0.5,
                      opacity: isDisabled ? 0.5 : 1,
                      cursor: isDisabled ? "not-allowed" : "pointer",
                      "&:hover": {
                        backgroundColor: isDisabled ? "transparent" : undefined,
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: !showText ? 0 : 2,
                        justifyContent: "center",
                      }}
                    >
                      <item.icon
                        className={`h-5 w-5 ${
                          isActive && !isDisabled ? "text-nemesis-primary" : ""
                        }`}
                      />
                    </ListItemIcon>
                    {showText && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flex: 1,
                        }}
                      >
                        <ListItemText
                          primary={item.title}
                          primaryTypographyProps={{ fontSize: 14 }}
                        />
                        {item.badge && (
                          <Chip
                            label={item.badge}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: 10,
                              fontWeight: 600,
                              backgroundColor: item.comingSoon
                                ? "#FCD34D"
                                : undefined,
                              color: item.comingSoon ? "#78350F" : undefined,
                            }}
                          />
                        )}
                      </Box>
                    )}
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            );
          })}
        </List>
        <Divider
          sx={{
            my: 1,
            mx: !showText ? 1 : 0,
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            opacity: 1,
            width: "auto",
            willChange: "margin, width",
          }}
        />
        {/* Grouped Navigation */}
        <List sx={{ px: 1 }}>
          {filteredGroups.map((group) => (
            <Box key={group.title}>
              {!showText ? (
                <>
                  <Tooltip title={group.title} placement="right">
                    <ListItem disablePadding>
                      <ListItemButton
                        onClick={(e) => handlePopoverToggle(e, group.title)}
                        sx={{
                          minHeight: 44,
                          justifyContent: "center",
                          px: 1.5,
                          borderRadius: 1,
                          mb: 0.5,
                        }}
                      >
                        <ListItemIcon
                          sx={{ minWidth: 0, justifyContent: "center" }}
                        >
                          <group.icon className="h-5 w-5" />
                        </ListItemIcon>
                      </ListItemButton>
                    </ListItem>
                  </Tooltip>
                  <Popover
                    open={openPopover && popoverGroupTitle === group.title}
                    anchorEl={anchorEl}
                    onClose={handlePopoverClose}
                    anchorOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "left",
                    }}
                    disableRestoreFocus
                    sx={{
                      "& .MuiPopover-paper": {
                        ml: 0.5,
                        boxShadow: 3,
                      },
                    }}
                  >
                    <Box sx={{ py: 1, minWidth: 200 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          px: 2,
                          py: 1,
                          fontWeight: 600,
                          color: "text.secondary",
                        }}
                      >
                        {group.title}
                      </Typography>
                      <List disablePadding>
                        {group.items.map((item) => {
                          const isActive = pathname === item.href;
                          return (
                            <ListItem key={item.href} disablePadding>
                              <ListItemButton
                                component={Link}
                                to={item.href}
                                selected={isActive}
                                onClick={handlePopoverClose}
                                sx={{
                                  minHeight: 40,
                                  px: 2,
                                  "&:hover": {
                                    backgroundColor: "action.hover",
                                  },
                                }}
                              >
                                <ListItemIcon sx={{ minWidth: 0, mr: 2 }}>
                                  <item.icon
                                    className={`h-4 w-4 ${
                                      isActive ? "text-nemesis-primary" : ""
                                    }`}
                                  />
                                </ListItemIcon>
                                <ListItemText
                                  primary={item.title}
                                  primaryTypographyProps={{ fontSize: 13 }}
                                />
                              </ListItemButton>
                            </ListItem>
                          );
                        })}
                      </List>
                    </Box>
                  </Popover>
                </>
              ) : (
                <>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => toggleGroup(group.title)}
                      sx={{
                        minHeight: 44,
                        px: 2,
                        borderRadius: 1,
                        mb: 0.5,
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 0, mr: 2 }}>
                        <group.icon className="h-5 w-5" />
                      </ListItemIcon>
                      <ListItemText
                        primary={group.title}
                        primaryTypographyProps={{ fontSize: 14 }}
                      />
                      {openGroups[group.title] ? (
                        <ExpandLess fontSize="small" />
                      ) : (
                        <ExpandMore fontSize="small" />
                      )}
                    </ListItemButton>
                  </ListItem>
                  <Collapse
                    in={openGroups[group.title]}
                    timeout="auto"
                    unmountOnExit
                  >
                    <List component="div" disablePadding>
                      {group.items.map((item) => {
                        const isActive = pathname === item.href;
                        return (
                          <ListItem key={item.href} disablePadding>
                            <ListItemButton
                              component={Link}
                              to={item.href}
                              selected={isActive}
                              sx={{
                                minHeight: 40,
                                pl: 4,
                                pr: 2,
                                borderRadius: 1,
                                mb: 0.5,
                              }}
                            >
                              <ListItemIcon sx={{ minWidth: 0, mr: 2 }}>
                                <item.icon
                                  className={`h-4 w-4 ${
                                    isActive ? "text-nemesis-primary" : ""
                                  }`}
                                />
                              </ListItemIcon>
                              <ListItemText
                                primary={item.title}
                                primaryTypographyProps={{ fontSize: 13 }}
                              />
                            </ListItemButton>
                          </ListItem>
                        );
                      })}
                    </List>
                  </Collapse>
                </>
              )}
            </Box>
          ))}
        </List>
        {/* Botón de expandir/colapsar al final del panel */}
        <Box
          sx={{
            marginTop: "auto",
            p: 1,
            display: "flex",
            justifyContent: showText ? "flex-end" : "center",
          }}
        >
          <Tooltip title={showText ? "Vista solo iconos" : "Expandir menú"} placement="right">
            <IconButton
              onClick={onTogglePin}
              size="small"
              sx={{
                color: "primary.main",
                backgroundColor: "#fff",
                border: "1px solid",
                borderColor: "divider",
                width: 32,
                height: 32,
                boxShadow: 1,
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                  boxShadow: 2,
                },
              }}
            >
              {showText ? (
                <PanelLeftClose className="h-4 w-4" />
              ) : (
                <PanelLeftOpen className="h-4 w-4" />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      </Drawer>
    </Box>
  );
}
